/*
 * ZOVERE welcome gift — the <zovere-gift> element used by
 * sections/zovere-gift.liquid.
 *
 * The scratch card opens once per visit after a short delay, and after
 * Shopify's cookie banner has been answered, unless the visitor closed it in
 * the last few days or has already revealed the gift.
 *
 * The card is a canvas over the gift. Scratching about half of it, or
 * pressing "Reveal without scratching", uncovers the code.
 */
(() => {
  if (customElements.get('zovere-gift')) return;

  const KEY_DONE = 'zovGift:done';
  const KEY_DISMISSED = 'zovGift:dismissed';
  const DAY = 86400000;
  const REVEAL_AT = 0.45;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Storage can be blocked (private mode, strict privacy settings); the card
  // then simply behaves as if this were a first visit.
  const store = {
    get(key) {
      try { return window.localStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, value); } catch (e) { /* ignore */ }
    },
  };

  class ZovereGift extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.zgift__panel');
      this.card = this.querySelector('[data-zgift-card]');
      if (!this.panel || !this.card) return;

      this.editor = this.hasAttribute('data-editor');
      this.delay = Number(this.dataset.delay) || 0;
      this.dismissDays = Number(this.dataset.dismissDays) || 14;
      this.revealed = false;

      this.onKeydown = this.onKeydown.bind(this);
      this.querySelectorAll('[data-zgift-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      if (this.editor) {
        this.startInEditor();
      } else {
        this.startOnStorefront();
      }
    }

    startInEditor() {
      if (this.hasAttribute('data-preview')) this.open(false);
      document.addEventListener('shopify:section:select', (event) => {
        if (this.closest('.shopify-section') === event.target) this.open(false);
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (this.closest('.shopify-section') === event.target) this.close(false);
      });
    }

    startOnStorefront() {
      if (store.get(KEY_DONE)) return;
      const dismissed = Number(store.get(KEY_DISMISSED));
      if (dismissed && Date.now() - dismissed < this.dismissDays * DAY) return;

      // The banner is injected after load, so it is checked once the delay
      // has passed rather than straight away.
      window.setTimeout(() => {
        this.whenCookieBannerAnswered((waited) => {
          window.setTimeout(() => this.open(true), waited ? 800 : 0);
        });
      }, this.delay);
    }

    // Shopify's cookie banner comes first; the gift waits until it has been
    // answered (or was never shown) so the two never stack up.
    whenCookieBannerAnswered(callback) {
      const bannerOpen = () => {
        // The banner is position: fixed, so offsetParent cannot be used.
        const banner = document.getElementById('shopify-pc__banner');
        if (!banner) return false;
        const style = window.getComputedStyle(banner);
        return style.display !== 'none' && style.visibility !== 'hidden' && banner.getBoundingClientRect().height > 0;
      };
      if (!bannerOpen()) {
        callback(false);
        return;
      }
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        document.removeEventListener('visitorConsentCollected', go);
        window.clearInterval(timer);
        callback(true);
      };
      document.addEventListener('visitorConsentCollected', go);
      const timer = window.setInterval(() => { if (!bannerOpen()) go(); }, 800);
    }

    open(animate) {
      // A close that is still fading out must not hide the card again.
      window.clearTimeout(this.closeTimer);
      this.lastFocus = document.activeElement;
      this.hidden = false;
      if (animate && !reducedMotion.matches) {
        // Let the hidden state paint before the transition starts.
        this.getBoundingClientRect();
      }
      this.classList.add('is-open');
      this.panel.focus({ preventScroll: true });
      document.addEventListener('keydown', this.onKeydown);
      this.setUpScratch();
    }

    close(remember = true) {
      if (this.hidden) return;
      if (remember && !this.editor && !this.revealed) {
        store.set(KEY_DISMISSED, String(Date.now()));
      }
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);

      const finish = () => {
        this.hidden = true;
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      };
      if (reducedMotion.matches) {
        finish();
      } else {
        this.closeTimer = window.setTimeout(finish, 500);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      // Keep keyboard focus inside the card while it is open.
      const focusable = Array.from(
        this.panel.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.closest('[hidden]') && !el.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setUpScratch() {
      if (this.scratchReady) return;
      this.scratchReady = true;

      this.canvas = this.querySelector('[data-zgift-foil]');
      this.prize = this.querySelector('.zgift__prize');
      this.revealBtn = this.querySelector('[data-zgift-reveal]');
      this.actions = this.querySelector('[data-zgift-actions]');
      this.copyBtn = this.querySelector('[data-zgift-copy]');
      this.code = this.querySelector('[data-zgift-code]');

      // Return to this exact page, including its query string (a selected
      // variant, a collection filter), after Shopify applies the code.
      const apply = this.querySelector('[data-zgift-apply]');
      if (apply) {
        const url = new URL(apply.href, window.location.origin);
        url.searchParams.set('redirect', window.location.pathname + window.location.search);
        apply.href = url.pathname + url.search;
      }

      this.prize.setAttribute('aria-hidden', 'true');
      this.revealBtn.addEventListener('click', () => this.reveal());
      if (this.copyBtn) this.copyBtn.addEventListener('click', () => this.copyCode());

      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) {
        this.reveal();
        return;
      }
      this.paintFoil();
      this.bindScratch();

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.revealed && !this.scratched) this.paintFoil();
      });
      this.resizeObserver.observe(this.card);
    }

    paintFoil() {
      const rect = this.card.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.ratio = ratio;
      this.canvas.width = Math.round(rect.width * ratio);
      this.canvas.height = Math.round(rect.height * ratio);

      const { ctx, canvas } = this;
      const w = canvas.width;
      const h = canvas.height;
      ctx.globalCompositeOperation = 'source-over';

      const base = ctx.createLinearGradient(0, 0, w, h);
      base.addColorStop(0, '#D9CFC3');
      base.addColorStop(0.45, '#E6D3B3');
      base.addColorStop(0.55, '#F1E6D2');
      base.addColorStop(1, '#C9B08A');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      // A soft diagonal sheen and a fine grain, so it reads as foil.
      const sheen = ctx.createLinearGradient(0, h, w, 0);
      sheen.addColorStop(0.3, 'rgba(255, 255, 255, 0)');
      sheen.addColorStop(0.5, 'rgba(255, 255, 255, 0.35)');
      sheen.addColorStop(0.7, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = 'rgba(48, 38, 58, 0.06)';
      const step = 6 * ratio;
      for (let y = step / 2; y < h; y += step) {
        for (let x = ((y / step) % 2) * (step / 2); x < w; x += step) {
          ctx.fillRect(x, y, ratio, ratio);
        }
      }

      ctx.fillStyle = 'rgba(48, 38, 58, 0.55)';
      ctx.font = `500 ${Math.round(26 * ratio)}px "Cormorant Garamond", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('ZOVERE', w / 2, h / 2 - 8 * ratio);
    }

    bindScratch() {
      const canvas = this.canvas;
      let last = null;
      let moves = 0;

      const point = (event) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: (event.clientX - rect.left) * this.ratio,
          y: (event.clientY - rect.top) * this.ratio,
        };
      };

      const scratchTo = (p) => {
        const { ctx } = this;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = 38 * this.ratio;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(last ? last.x : p.x, last ? last.y : p.y);
        ctx.lineTo(p.x + 0.01, p.y);
        ctx.stroke();
        last = p;
        moves += 1;
        if (moves % 10 === 0 && this.clearedShare() >= REVEAL_AT) this.reveal();
      };

      canvas.addEventListener('pointerdown', (event) => {
        if (this.revealed) return;
        event.preventDefault();
        canvas.setPointerCapture(event.pointerId);
        this.scratched = true;
        this.card.classList.add('is-scratching');
        last = null;
        scratchTo(point(event));
      });

      canvas.addEventListener('pointermove', (event) => {
        if (this.revealed || !canvas.hasPointerCapture(event.pointerId)) return;
        scratchTo(point(event));
      });

      const stop = (event) => {
        if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
        last = null;
        if (!this.revealed && this.clearedShare() >= REVEAL_AT) this.reveal();
      };
      canvas.addEventListener('pointerup', stop);
      canvas.addEventListener('pointercancel', stop);
    }

    // Share of the foil already scratched away, sampled on a coarse grid.
    clearedShare() {
      const { width, height } = this.canvas;
      if (!width || !height) return 0;
      const data = this.ctx.getImageData(0, 0, width, height).data;
      const stride = 12 * this.ratio;
      let total = 0;
      let clear = 0;
      for (let y = 0; y < height; y += stride) {
        for (let x = 0; x < width; x += stride) {
          total += 1;
          if (data[(Math.floor(y) * width + Math.floor(x)) * 4 + 3] < 32) clear += 1;
        }
      }
      return total ? clear / total : 0;
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.card.classList.add('is-revealed');
      this.prize.removeAttribute('aria-hidden');
      this.revealBtn.hidden = true;
      this.actions.hidden = false;

      const heading = this.querySelector('[data-zgift-after]');
      if (heading && heading.dataset.zgiftAfter) heading.textContent = heading.dataset.zgiftAfter;
      const text = this.querySelector('[data-zgift-text]');
      if (text) text.hidden = true;

      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (!this.editor) store.set(KEY_DONE, '1');
    }

    copyCode() {
      const text = this.code.textContent.trim();
      const done = () => {
        const label = this.copyBtn.textContent;
        this.copyBtn.textContent = this.copyBtn.dataset.copied || 'Copied';
        window.setTimeout(() => { this.copyBtn.textContent = label; }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, () => this.selectCode());
      } else {
        this.selectCode();
      }
    }

    selectCode() {
      const range = document.createRange();
      range.selectNodeContents(this.code);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  customElements.define('zovere-gift', ZovereGift);
})();
